import { motion } from "motion/react";
import {
  Archive,
  Bell,
  CalendarClock,
  Hourglass,
  Lightbulb,
  type LucideIcon,
  Sparkles,
} from "lucide-react";
import { memo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

import { GradientDefinition } from "@/components/ui/gradient-def.tsx";
import { clickableCardClass } from "@/components/threads/shared.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { cn } from "@/lib/utils.ts";
import { type MuteChoice, muteConfirm } from "@/shared/muteChoices.ts";
import { type TaskIndicator } from "@/shared/taskFilterLabels.ts";
import {
  ASKED_DISMISS_REASONS,
  ASKED_DROP_REASONS,
  DISMISS_LABEL,
  KEEP_WAITING,
  TASK_DONE_SHORT,
} from "@/shared/taskRowCopy.ts";
import {
  AFFIRMATIVE_LABEL,
  type AffirmativeHandlers,
  type AffirmativeKind,
  affirmativeAccessibleName,
  answerableThreadId,
  canReopen,
  canSnooze,
  snoozeUntil,
  taskAffirmative,
} from "@/shared/userTaskActions.ts";
import { snoozedLine } from "@/shared/snoozedLine.ts";
import { dueLine } from "@/shared/userTaskDue.ts";
import { isClosedStatus, type UserTask, type UserTaskSource } from "@/types/userTasks.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import {
  shownStatus,
  useAnswerSilence,
  useHandToOkthx,
  useAnswerTriage,
  useSetTaskContext,
  useSetUserTaskStatus,
  useHoldTask,
  useTakeBack,
} from "./api.ts";
import { useAskAbout } from "./AskAboutTask.tsx";
import { sourceHref } from "./sourceLinks.ts";
import { ContextEditor } from "./ContextEditor.tsx";
import { CommitMenu } from "./CommitMenu.tsx";
import { HandOffDialog } from "./HandOffDialog.tsx";
import { LabelChip } from "./LabelChip.tsx";
import { RowButton } from "./RowButton.tsx";
import { ReasonMenu } from "./ReasonMenu.tsx";
import { RowMoreMenu } from "./RowMoreMenu.tsx";
import { type FormDraft, TaskAnswerDialog } from "./TaskAnswerDialog.tsx";
import { TaskRulePanel } from "./TaskRulePanel.tsx";
import { useMuteSender } from "./useMuteSender.ts";
import { useTaskArrival } from "./useTaskArrival.ts";

const MIRROR_LABEL: Record<string, string> = { todoist: "Todoist" };

/**
 * The mark a card's kind wears, and its colour.
 *
 * Three tiers, read by what the card asks of the user rather than by where the
 * task is in its life: brand purple for the one kind that is blocked on them,
 * grey for the two that are in flight and owe them nothing, amber for a
 * suggestion — new and worth a look, but not yet theirs. Later and Closed take
 * no mark at all, being the record.
 *
 * Purple matches thread status, where it means the same thing. The other two
 * tiers deliberately do not: a thread okthx is working wears amber there, and
 * here that hue is spent on the suggestion instead, because a task list is
 * scanned for what to pick up next and the new thing is what earns the colour.
 *
 * Okthx wears sparkles in the brief gradient — the one a meeting okthx has
 * written a brief for wears on the calendar. It is the one kind that is okthx
 * working rather than the user, and the treatment already means exactly that
 * everywhere else in the app. Carried by the glyph and the words themselves
 * rather than by a ring around them, so it reads as okthx's own hand on the
 * work rather than as another chip on a row that has enough of them.
 *
 * `className` is the flat colour for the rest; this one carries the brand
 * gradient utility instead, since a gradient is not a colour a token can hold.
 */
export const INDICATOR: Record<
  string,
  { Icon: LucideIcon; className: string; gradientIcon?: boolean }
> = {
  needsYou: { Icon: Bell, className: "text-primary" },
  okthx: {
    Icon: Sparkles,
    className: "gradient-brand-purple",
    gradientIcon: true,
  },
  // Hourglass rather than a clock, the distinction thread status already draws:
  // a clock reads as time running, and this is time parked. The task chips
  // spend a clock on "due" besides.
  waiting: { Icon: Hourglass, className: "text-zinc-700" },
  suggested: { Icon: Lightbulb, className: "text-amber-600" },
  // Off the list but retrievable, which is what the thread list means by the
  // same glyph. Not a tick: this group holds a task that was dropped as well
  // as one that was finished, and a tick would call them both done.
  closed: { Icon: Archive, className: "text-muted-foreground" },
  // Parked until a date. A calendar rather than the hourglass beside it:
  // waiting is time passing with nothing to do, and this is a day already
  // chosen.
  later: { Icon: CalendarClock, className: "text-muted-foreground" },
};

/** What kind of task this row is: the mark, then the word for it. */
export const GroupMark = ({ group }: { group: TaskIndicator }) => {
  const { Icon, className = "text-muted-foreground", gradientIcon } = INDICATOR[group.key] ?? {};
  return (
    <span
      className={cn("flex items-center gap-1.5 text-xs font-medium", !gradientIcon && className)}
    >
      {/* `background-clip: text` reaches text and nothing else, so the glyph
          beside it inherits `currentColor` — transparent — and vanishes. The
          shared definition gives its stroke the same gradient instead, over
          its own 24x24 box, so the glyph shows the whole sweep too. */}
      {gradientIcon && <GradientDefinition />}
      {Icon && (
        <Icon
          className={cn("size-3.5", gradientIcon && "gradient-brand-purple-stroke")}
          aria-hidden
        />
      )}
      {/* The words carry the gradient on a box of their own rather than
          inheriting the row's. A background painted across glyph and gap as
          well starts the sweep before the first letter, so the words began a
          sixth of the way along and read as purple throughout — the blue end
          of it was spent on the space beside the mark. */}
      {gradientIcon ? <span className={className}>{group.label}</span> : group.label}
    </span>
  );
};

/** The source a close rests on: "Why was this closed?" answered on the row. */
const ClosedByLink = ({ source }: { source: UserTaskSource }) => {
  const href = sourceHref(source);
  const label = source.title ?? "the source";
  return (
    <>
      {" — "}
      {href ? (
        <Link href={href} className="relative z-10 underline underline-offset-2">
          {label}
        </Link>
      ) : (
        label
      )}
    </>
  );
};

/**
 * What a mute asks before it happens. The words are `muteConfirm`'s, shared
 * with the phone; this is the box they go in. Mounted only while asking, as
 * the other dialogs on this row are, so it opens on the choice just made.
 */
const MuteConfirm = ({
  choice,
  onCancel,
  onMute,
}: {
  choice: MuteChoice;
  onCancel: () => void;
  onMute: () => void;
}) => {
  const { title, body } = muteConfirm(choice);
  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-statsig-name="tasks-mute-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-statsig-name="tasks-mute-confirm" onClick={onMute}>
            Mute
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/** The event each one-tap affirmative logs, by the kind `taskAffirmative` picks. */
const AFFIRMATIVE_STATSIG: Record<Exclude<AffirmativeKind, "commit">, string> = {
  keep: "tasks-triage-keep",
  answer: "tasks-answer",
  takeBack: "tasks-take-back",
  toOkthx: "tasks-to-okthx",
  done: "tasks-done",
  reopen: "tasks-reopen",
};

/**
 * The one answer that moves a task forward. Which one it is, and what it is
 * called, come from `taskAffirmative` and `AFFIRMATIVE_LABEL`, so this row and
 * the phone's answer the same question on the same task in the same words;
 * the row says what each one does.
 */
const Affirmative = ({
  task,
  disabled,
  handlers,
  onCommit,
  onHold,
}: {
  task: UserTask;
  disabled: boolean;
  handlers: AffirmativeHandlers;
  /** A commitment picks a day first, so it is a menu rather than a button. */
  onCommit: (snoozedUntilMs: number) => void;
  /** The menu's other answer: the reader is already on it. */
  onHold: () => void;
}) => {
  const kind = taskAffirmative(task);
  if (kind === null) return null;
  if (kind === "commit") {
    return <CommitMenu task={task} disabled={disabled} onCommit={onCommit} onHold={onHold} />;
  }
  return (
    <RowButton
      label={AFFIRMATIVE_LABEL[kind]}
      ariaLabel={affirmativeAccessibleName(kind, task)}
      statsigName={AFFIRMATIVE_STATSIG[kind]}
      disabled={disabled}
      onClick={handlers[kind]}
    />
  );
};

/**
 * What the user can do with a row, by status. A suggested task is answered
 * (Keep, Dismiss, Same as…). A quiet wait gets its check-in answers. An open
 * task is finished, put down or dropped, and can be handed to okthx or taken
 * back. A Later or closed one can come back.
 *
 * One change at a time: every action waits while another is in flight, so two
 * taps cannot race each other to the server.
 */
export const RowActions = ({
  task,
  surface = "row",
  onEditContext,
}: {
  task: UserTask;
  /**
   * Where this bar is drawn. A row keeps the quieter answers in an overflow
   * because it has no room for them; a page has the room and says them
   * outright, so the overflow would only repeat what is already there.
   */
  surface?: "row" | "page";
  /** Opens the row's context editor, which this bar does not itself draw. */
  onEditContext?: () => void;
}) => {
  const triage = useAnswerTriage();
  const setStatus = useSetUserTaskStatus();
  const silence = useAnswerSilence();
  const takeBack = useTakeBack();
  const hold = useHoldTask();
  const handoff = useHandToOkthx();
  const handOffContext = useSetTaskContext();
  // Handing a task over goes through the note first: see `HandOffDialog`.
  const [handingOff, setHandingOff] = useState(false);
  // Whether the hand-off is still wanted once the note has saved: the reader
  // can cancel while it saves, and a dialog they closed is a decision the
  // save finishing must not overrule.
  const handOffWanted = useRef(false);
  /**
   * Saves the note if the reader changed it, then hands the task over.
   *
   * Sequential on purpose: the note is what okthx reads the task by, and
   * starting the work before the correction has landed would have it read the
   * version the reader just rewrote. A save that fails stops here, with the
   * dialog open on the reader's words and the toast saying why — handing over
   * anyway would start the work on the note they had just corrected.
   *
   * A field cleared to nothing is left alone: the dialog adds to the note,
   * and the server keeps one from being emptied.
   */
  const handOff = async (note: string) => {
    handOffWanted.current = true;
    const written = note.trim();
    if (written && written !== (task.context?.text ?? "").trim()) {
      try {
        await handOffContext.mutateAsync({ taskId, text: written });
      } catch {
        return;
      }
    }
    if (!handOffWanted.current) return;
    setHandingOff(false);
    handoff.mutate({ taskId });
  };
  const cancelHandOff = () => {
    handOffWanted.current = false;
    setHandingOff(false);
  };
  const ask = useAskAbout();
  const muteSender = useMuteSender();
  // A mute reaches backwards, so it asks first: see `muteConfirm`.
  const [muting, setMuting] = useState<MuteChoice | null>(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [answering, setAnswering] = useState(false);
  // The answer dialog's form draft outlives the dialog: Expand closes it to
  // uncover the canvas, and reopening it finds the answers where they were.
  const [answerDraft, setAnswerDraft] = useState<FormDraft | null>(null);
  // The thread okthx is waiting on the reader in, where there is one.
  const answerThreadId = answerableThreadId(task);
  const mutations = [triage, setStatus, silence, takeBack, hold];
  const busy =
    mutations.some((m) => m.isPending) ||
    handoff.isPending ||
    ask.isPending ||
    muteSender.isPending;
  const taskId = task.id;
  // What this row shows: a task moved since — by okthx, or in another tab — is a 409.
  const fromStatus = shownStatus(task.status);
  // The time itself is taken at the click: a page left open past midnight
  // would send a morning that has passed.
  const snoozable = canSnooze(task);
  // A rule saved from Dismiss or Drop answers this task too: it is the kind
  // the user just said is not a task.
  const answerAsNotATask = () => {
    setRuleOpen(false);
    if (task.status === "suggested") {
      triage.mutate({ taskId, answer: "dismiss", reason: "notATask" });
    } else {
      setStatus.mutate({ taskId, fromStatus, status: "cancelled", reason: "notATask" });
    }
  };

  // Dismiss leads, then Done, then the affirmative: the two that end a task
  // sit together on the left, and the one that moves it forward sits furthest
  // from them.
  const affirmative = (
    <Affirmative
      task={task}
      disabled={busy}
      handlers={{
        keep: () => handoff.mutate({ taskId }),
        // In place rather than navigating: going to the thread answered the
        // question by throwing away the reader's place in the list they were
        // working.
        answer: () => setAnswering(true),
        takeBack: () => takeBack.mutate({ taskId }),
        toOkthx: () => setHandingOff(true),
        done: () => setStatus.mutate({ taskId, fromStatus, status: "done" }),
        reopen: () => setStatus.mutate({ taskId, fromStatus, status: "open" }),
      }}
      // A promise, not a postponement: the day becomes the task's due date.
      onCommit={(snoozedUntilMs) =>
        setStatus.mutate({ taskId, fromStatus, status: "later", snoozedUntilMs, committed: true })
      }
      onHold={() => hold.mutate({ taskId })}
    />
  );
  let actions: React.ReactNode = null;
  if (task.status === "suggested") {
    const duplicate = task.possibleDuplicateOf;
    actions = (
      <>
        <ReasonMenu
          label={DISMISS_LABEL}
          subject={task.title}
          statsigName="tasks-triage-dismiss"
          reasons={ASKED_DISMISS_REASONS}
          disabled={busy}
          duplicate={duplicate}
          onPick={(reason, reasonText) =>
            triage.mutate({ taskId, answer: "dismiss", reason, reasonText })
          }
          onSameAs={(sameAsTaskId) => triage.mutate({ taskId, answer: "sameAs", sameAsTaskId })}
          onRemove={() => triage.mutate({ taskId, answer: "dismiss" })}
        />
        {affirmative}
      </>
    );
  } else if (
    task.status === "open" &&
    task.assignee === "user" &&
    task.waitingOn &&
    task.checkIn?.kind === "silence"
  ) {
    actions = (
      <>
        <RowButton
          label={KEEP_WAITING}
          statsigName="tasks-silence-still-waiting"
          disabled={busy}
          onClick={() => silence.mutate({ taskId, answer: "stillWaiting" })}
        />
        {/* The check-in asks whether the wait is over. Answering that it is
            finishes the task, which is what Done means everywhere else. */}
        <RowButton
          label={TASK_DONE_SHORT}
          statsigName="tasks-silence-got-it"
          disabled={busy}
          onClick={() => silence.mutate({ taskId, answer: "gotIt" })}
        />
        {affirmative}
      </>
    );
  } else if (task.status === "open") {
    // Done is already drawn here, so an affirmative that is also Done — a
    // task due before any day it could be committed to — is not drawn twice.
    const forward = taskAffirmative(task) === "done" ? null : affirmative;
    actions = (
      <>
        <ReasonMenu
          label={DISMISS_LABEL}
          subject={task.title}
          statsigName="tasks-drop"
          reasons={ASKED_DROP_REASONS}
          disabled={busy}
          onPick={(reason, reasonText) =>
            setStatus.mutate({ taskId, fromStatus, status: "cancelled", reason, reasonText })
          }
          onRemove={() => setStatus.mutate({ taskId, fromStatus, status: "cancelled" })}
          onLater={
            snoozable
              ? () => {
                  const snoozedUntilMs = snoozeUntil(task);
                  if (snoozedUntilMs === undefined) return;
                  setStatus.mutate({ taskId, fromStatus, status: "later", snoozedUntilMs });
                }
              : undefined
          }
        />
        <RowButton
          label={TASK_DONE_SHORT}
          statsigName="tasks-done"
          disabled={busy}
          onClick={() => setStatus.mutate({ taskId, fromStatus, status: "done" })}
        />
        {forward}
      </>
    );
  } else if (task.status === "later") {
    // Parked on a date, and live: it can still be finished early or dropped,
    // and it comes back on its day by itself. Checked ahead of the reopen
    // branch below, which would otherwise catch `later` and offer nothing but
    // Reopen, an answer a task that is only waiting does not need.
    actions = (
      <>
        <ReasonMenu
          label={DISMISS_LABEL}
          subject={task.title}
          statsigName="tasks-drop"
          reasons={ASKED_DROP_REASONS}
          disabled={busy}
          onPick={(reason, reasonText) =>
            setStatus.mutate({ taskId, fromStatus, status: "cancelled", reason, reasonText })
          }
          onRemove={() => setStatus.mutate({ taskId, fromStatus, status: "cancelled" })}
        />
        {affirmative}
      </>
    );
  } else if (canReopen(task)) {
    actions = affirmative;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {actions}
        {/* `ml-auto` holds the right edge even when the left half is empty, so
            the overflow sits in the same place on every card. */}
        <div className="ml-auto">
          {/* Writing a note and opening a conversation are both worth doing and
              neither is what the row is for: the answers are. Two more outlined
              buttons beside them read as two more answers. One glyph holds them
              instead, in the corner the bubble used to have. */}
          {/* Dropped on a page, which spells all four of these out: the note
              has its own Add, and asking, muting and rules are buttons under a
              rule. A menu whose every item is already on the screen is a click
              that answers nothing. */}
          {surface === "row" && (
            <RowMoreMenu
              task={task}
              disabled={busy}
              onEditContext={() => onEditContext?.()}
              onAsk={() => ask.mutate({ taskId })}
              onMute={setMuting}
              onRule={() => setRuleOpen(true)}
            />
          )}
        </div>
      </div>
      {ruleOpen && (
        <TaskRulePanel
          taskId={taskId}
          onSaved={answerAsNotATask}
          onCancel={() => setRuleOpen(false)}
        />
      )}
      {answering && answerThreadId !== null && (
        <TaskAnswerDialog
          title={task.title}
          threadId={answerThreadId}
          draft={answerDraft}
          onDraft={setAnswerDraft}
          onClose={() => setAnswering(false)}
          onAnswered={() => {
            setAnswerDraft(null);
            setAnswering(false);
          }}
        />
      )}
      {handingOff && (
        <HandOffDialog
          context={task.context}
          subject={task.title}
          saving={handOffContext.isPending || handoff.isPending}
          onClose={cancelHandOff}
          onHandOff={handOff}
        />
      )}
      {muting && (
        <MuteConfirm
          choice={muting}
          onCancel={() => setMuting(null)}
          onMute={() => muteSender.mute(muting.value)}
        />
      )}
    </div>
  );
};

/** The task's due date, marked when it is overdue. */
export const DueLine = ({ task }: { task: UserTask }) => {
  const due = dueLine(task, Date.now());
  if (!due) return null;
  return (
    // Base plus an override on the label alone, as native draws it: the two
    // words are the news, and the date is read at the weight of any subtext.
    <p className="text-xs text-muted-foreground">
      <span className={cn(due.overdue && "font-semibold")}>{due.label}</span> {due.when}
    </p>
  );
};

/** Where the focused task stands, and the user's correction to it. */
export const TaskContext = ({
  task,
  open,
  onOpenChange,
}: {
  task: UserTask;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const setContext = useSetTaskContext();
  return (
    <ContextEditor
      context={task.context}
      saving={setContext.isPending}
      onSave={(text) => setContext.mutateAsync({ taskId: task.id, text })}
      statsigName="tasks-task-context"
      open={open}
      onOpenChange={onOpenChange}
      // The card is the one control; the note's words stay, its links go.
      links={false}
    />
  );
};

/**
 * One task. Its title opens it at `/tasks/:id`; the task that link opened is
 * outlined and scrolled to, and shows where it stands with a way to correct it,
 * so a tap on a brief's line lands on the row to answer.
 */
export const UserTaskRow = memo(function UserTaskRow({
  task,
  group,
  focused = false,
  inLabelId,
}: {
  task: UserTask;
  /**
   * Which group this row belongs to, drawn only where rows of several groups
   * share one list. Omitted on a page whose rows are all of one group, so the
   * structure does not shout over the tasks.
   */
  group?: TaskIndicator;
  focused?: boolean;
  /**
   * The label whose page this row is on, so its own chip is not drawn. A chip
   * pointing at the page you are reading is not a link, and it spends the
   * header's narrowest room saying what the heading above already says.
   */
  inLabelId?: string;
}) {
  // Where the reader is, for the page to come back to: this list, or a label's.
  const [location] = useLocation();
  const { ref, flash, onAnimationEnd } = useTaskArrival<HTMLLIElement>(task.id, focused);
  const snoozed = snoozedLine(task);
  const chips = (task.labels ?? []).filter((label) => label.id !== inLabelId);
  const [editingContext, setEditingContext] = useState(false);
  return (
    <motion.li
      ref={ref}
      // `position`, not a full layout animation: a full one scale-projects
      // width changes, which the thread cards learned the hard way. The rows
      // only ever move up and down.
      layout="position"
      // Only the exit is animated. A card arriving has nothing to slide out
      // of the way of, and fading every row in on first paint makes the list
      // feel slower to arrive than it is.
      //
      // It leaves the flow as it goes rather than collapsing its own height.
      // Collapsing resizes the scroller's content on every frame of the
      // animation, and a scrollbar that reacts to content size — which the
      // overlay ones do — flashes down the side for as long as it runs. Out
      // of flow, the height changes once and the gap closes by the rows below
      // sliding up, which is a transform and costs the scroller nothing.
      exit={{ opacity: 0, position: "absolute", width: "100%" }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className={cn(
        // On the flag, not on `focused`: the route keeps naming this task, so a
        // glow keyed on it fires again on every refresh and every return from
        // another filter. The glow says where the list just took you, and that
        // is true once.
        flash && "animate-task-flash",
        // The hover shadow is this app's clickable signal, and the card is the
        // control now — so it wears the same elevation Connectors, Contacts
        // and the thread cards wear. `relative` so the link that carries the
        // click can stretch across it: see the note on it below.
        clickableCardClass,
        // `isolate` keeps the controls' `z-10` inside the card. They carry it
        // to clear the stretched link, which is a card-local fight — but z is
        // global, so without a stacking context here they tied with the
        // sticky filter bar and won on document order, painting over it.
        "relative isolate flex flex-col gap-2",
        // A task that is finished, or put down until a date, is the record
        // rather than the work, so its card lets the page through and its
        // words sit back with it. The actions keep full strength: a record is
        // still acted on, and a faded control reads as a disabled one.
        // A scheduled task does not fade: it is live work you have bought time
        // for, where a closed one is the record of work that is over.
        isClosedStatus(task.status) && "bg-white/60",
      )}
      data-task-id={task.id}
      onAnimationEnd={onAnimationEnd}
      aria-current={focused ? "true" : undefined}
    >
      {/* The words fade with the surface; the actions below do not. The
          wrapper carries the card's own column so the spacing is unchanged. */}
      <div className={cn("flex flex-col gap-1.5", isClosedStatus(task.status) && "opacity-50")}>
        {/* The kind of task and who holds it share one line above the title: two
            short labels that each answer "is this mine to read?", where stacked
            they cost two rows and read as part of the sentence. Who holds it sits
            right, away from the title's ragged left. The same arrangement the
            native row uses, so a task reads the same on either surface. */}
        {/* The kind of task sets this row's height and the marks centre on it.
            They are the taller of the two, so left to themselves they would
            set the height instead and push the words down the card — hence the
            negative margin below, which takes the pills back out of the
            measurement they would otherwise win. */}
        <div className="mb-0.5 flex items-center gap-2">
          {group && <GroupMark group={group} />}
          {/* The labels and the mirror share the header's right edge with the
              kind of task on its left: all three say what this row is, and
              spelling them on separate lines spent a row each on two words. */}
          {/* `z-10`, not `relative` alone: the stretched link paints over
              anything earlier in the card, and these sit above the title.
              Every control on the card carries it, so moving one cannot
              silently drop it behind the link. */}
          <div className="relative z-10 -my-1 ml-auto flex min-w-0 shrink flex-wrap items-center justify-end gap-1.5">
            {task.mirroredTo && (
              <span
                className="shrink truncate text-right text-xs text-muted-foreground"
                data-mirrored-to={task.mirroredTo}
              >
                In {MIRROR_LABEL[task.mirroredTo] ?? task.mirroredTo}
              </span>
            )}
            {chips.map((label) => (
              <LabelChip
                key={label.id}
                label={label}
                statsigName="tasks-label-chip"
                className="max-w-full shrink-0 truncate"
              />
            ))}
          </div>
        </div>
        {/* A link again. It was demoted to a heading because `/tasks/:id` only
            rang this row and revealed its note, which every card draws anyway,
            so the trip led nowhere — and now it leads to the task's own page.
            A heading still, so a reader can jump the list by title; the link
            is inside it. */}
        <h3 className="text-sm font-semibold">
          {/* The whole card opens the task, through one link stretched over it
              rather than a handler on the card: an anchor keeps middle-click,
              open-in-new-tab and the status bar, which a clickable div throws
              away. Wrapping the card in an anchor would put buttons inside a
              link, which is invalid and breaks them.

              Its words are the title, but it neither reads as a link nor
              underlines: the card is the control, and a link inside a
              clickable card offers the same trip twice. */}
          <Link
            href={userTaskHref(task.id)}
            state={{ from: location }}
            className="after:absolute after:inset-0 after:content-['']"
            data-statsig-name="tasks-open-detail"
          >
            {task.title}
          </Link>
        </h3>
        <DueLine task={task} />
        {/* Always, not only on the focused row: a note the user wrote is part
            of what the card says, and hiding it behind opening the task made
            the title a control that did not look like one. */}
        {/* Above the stretched link only while it is being edited, when its
            field and Save have to take their own clicks. Read, the note is
            words, and a four-line note was covering half the card; clicks
            fall through to the card's link, except on the note's own links. */}
        <div
          className={cn(
            "relative z-10",
            !editingContext && "pointer-events-none [&_a]:pointer-events-auto",
          )}
        >
          <TaskContext task={task} open={editingContext} onOpenChange={setEditingContext} />
        </div>
        {/* A new task's context is seeded from its description, so a suggestion
            says the same sentence in both fields until someone edits one.
            Drawn once while they agree. */}
        {task.status === "suggested" &&
          task.description &&
          task.description !== task.context?.text && (
            <p className="text-xs text-muted-foreground">{task.description}</p>
          )}
        {isClosedStatus(task.status) && (
          <p className="text-xs text-muted-foreground" data-closed-by={task.closedBy?.source?.urn}>
            {task.status === "done" ? "Done" : "Cancelled"}
            {task.closedReason ? `: ${task.closedReason}` : ""}
            {task.closedBy?.source && <ClosedByLink source={task.closedBy.source} />}
          </p>
        )}
        {/* The same treatment as a suggestion's description: both are okthx
            explaining the row in a sentence, and this one was the only
            secondary line on the card drawn in the title's colour. */}
        {task.status === "open" && task.checkIn && (
          <p className="text-xs text-muted-foreground" data-check-in={task.checkIn.kind}>
            {task.checkIn.text}
          </p>
        )}
        {snoozed && <p className="text-xs text-muted-foreground">{snoozed}</p>}
      </div>
      {/* Above it too, so the card's own answers still answer. */}
      <div className="relative z-10">
        <RowActions task={task} onEditContext={() => setEditingContext(true)} />
      </div>
    </motion.li>
  );
});
